import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import API from "../config/apiConfig";

// Debug: Log the API URL being used
console.log("🔧 AppContext Initialized");
console.log("📡 API Endpoint:", API);

// Admin Auth Context (separate from regular user auth)
const AdminAuthContext = createContext(null);

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  return (
    context || {
      adminUser: null,
      adminToken: null,
      adminLoading: false,
      adminLogin: () => {},
      adminLogout: () => {},
      isAdminAuthenticated: false,
    }
  );
}

export function AdminAuthProvider({ children }) {
  const [state, setState] = useState({
    adminUser: null,
    adminToken: localStorage.getItem("admin_token"),
    adminLoading: true,
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((res) => {
          if (res.data.is_admin) {
            setState({ adminUser: res.data, adminToken: token, adminLoading: false });
          } else {
            localStorage.removeItem("admin_token");
            setState({ adminUser: null, adminToken: null, adminLoading: false });
          }
        })
        .catch(() => {
          localStorage.removeItem("admin_token");
          setState({ adminUser: null, adminToken: null, adminLoading: false });
        });
    } else {
      setState((s) => ({ ...s, adminLoading: false }));
    }
  }, []);

  const adminLogin = async (email, password) => {
    const url = `${API}/auth/admin-login`;
    console.log("🔐 ADMIN LOGIN ATTEMPT");
    console.log("📡 Full URL:", url);
    console.log("📨 Payload:", { email, password });
    const res = await axios.post(url, { email, password });
    localStorage.setItem("admin_token", res.data.access_token);
    localStorage.setItem("admin_email", email);
    setState({
      adminUser: res.data.user,
      adminToken: res.data.access_token,
      adminLoading: false,
    });
    return res.data;
  };

  const adminLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    setState({ adminUser: null, adminToken: null, adminLoading: false });
  };

  return (
    <AdminAuthContext.Provider
      value={{
        ...state,
        adminLogin,
        adminLogout,
        isAdminAuthenticated: !!state.adminUser,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  return (
    context || {
      user: null,
      token: null,
      loading: false,
      login: () => {},
      register: () => {},
      logout: () => {},
      isAuthenticated: false,
    }
  );
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    user: null,
    token: localStorage.getItem("token"),
    loading: true,
  });

  // Check token on mount
  useEffect(() => {
    if (state.token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${state.token}`;
      axios
        .get(`${API}/auth/me`)
        .then((res) =>
          setState((s) => ({ ...s, user: res.data, loading: false })),
        )
        .catch(() => {
          localStorage.removeItem("token");
          setState({ user: null, token: null, loading: false });
        });
    } else {
      setState((s) => ({ ...s, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${res.data.access_token}`;
    setState({
      user: res.data.user,
      token: res.data.access_token,
      loading: false,
    });
    return res.data;
  };

  // adminLogin moved to AdminAuthProvider - kept here for backward compat
  const adminLogin = async (email, password) => {
    const url = `${API}/auth/admin-login`;
    const res = await axios.post(url, { email, password });
    localStorage.setItem("admin_token", res.data.access_token);
    return res.data;
  };

  const register = async (name, email, password, phone) => {
    const res = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      phone,
    });
    localStorage.setItem("token", res.data.access_token);
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${res.data.access_token}`;
    setState({
      user: res.data.user,
      token: res.data.access_token,
      loading: false,
    });
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setState({ user: null, token: null, loading: false });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        adminLogin,
        register,
        logout,
        isAuthenticated: !!state.user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Cart Context
const CartContext = createContext(null);

export function useCart() {
  const context = useContext(CartContext);
  return (
    context || {
      items: [],
      total: 0,
      itemCount: 0,
      isOpen: false,
      setIsOpen: () => {},
      addToCart: () => {},
      updateQuantity: () => {},
      removeItem: () => {},
      clearCart: () => {},
      fetchCart: () => {},
    }
  );
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    if (auth.token) {
      axios
        .get(`${API}/cart`)
        .then((res) => setItems(res.data))
        .catch(() => setItems([]));
    } else {
      setItems([]);
    }
  }, [auth.token]);

  const addToCart = async (productId, quantity) => {
    if (!auth.token) throw new Error("Please login");
    await axios.post(`${API}/cart/add`, {
      product_id: productId,
      quantity: quantity || 1,
    });
    const res = await axios.get(`${API}/cart`);
    setItems(res.data);
  };

  const updateQuantity = async (productId, quantity) => {
    await axios.post(`${API}/cart/update`, { product_id: productId, quantity });
    const res = await axios.get(`${API}/cart`);
    setItems(res.data);
  };

  const removeItem = async (productId) => {
    await axios.delete(`${API}/cart/${productId}`);
    const res = await axios.get(`${API}/cart`);
    setItems(res.data);
  };

  const clearCart = async () => {
    await axios.delete(`${API}/cart`);
    setItems([]);
  };

  let total = 0;
  let itemCount = 0;
  for (let i = 0; i < items.length; i++) {
    total += items[i].price * items[i].quantity;
    itemCount += items[i].quantity;
  }

  return (
    <CartContext.Provider
      value={{
        items,
        total,
        itemCount,
        isOpen,
        setIsOpen,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Combined Provider
export function AppProvider({ children }) {
  return (
    <AdminAuthProvider>
      <AuthProvider>
        <CartProvider>{children}</CartProvider>
      </AuthProvider>
    </AdminAuthProvider>
  );
}
